'use client';

import { useState, useEffect, useCallback } from 'react';
import { startOfDay, format } from 'date-fns';
import { useDeviceType } from '@/hooks/use-device-type';
import { cleanupOldClassSessions } from '@/helpers/class';

interface ClassDateFilterState {
  selectedDate: Date | null;
  selectedInstructorIds: string[];
  isRestoredFromStorage: boolean;
}

const getStorageKey = (classSlug: string) =>
  `mokoboko_class_filter_${classSlug}`;

const getDefaultState = (): ClassDateFilterState => {
  return {
    selectedDate: null,
    selectedInstructorIds: [],
    isRestoredFromStorage: false,
  };
};

export const useClassDateFilter = (classSlug: string) => {
  const deviceType = useDeviceType();
  const [state, setState] = useState<ClassDateFilterState>(() => {
    return getDefaultState();
  });
  const [isInitialized, setIsInitialized] = useState(false);

  // Clean up old class sessions when component mounts
  useEffect(() => {
    cleanupOldClassSessions(classSlug);
  }, [classSlug]);

  // Load state from sessionStorage on mount
  useEffect(() => {
    try {
      const storageKey = getStorageKey(classSlug);
      const savedState = sessionStorage.getItem(storageKey);
      if (savedState) {
        const parsedState = JSON.parse(savedState);
        // Convert date string back to Date object
        const restoredState = {
          ...parsedState,
          selectedDate: parsedState.selectedDate
            ? new Date(parsedState.selectedDate)
            : null,
          isRestoredFromStorage: true,
        };
        setState(restoredState);
      }
    } catch (error) {
      console.error('Error loading class date filter state:', error);
      // Clear invalid state
      const storageKey = getStorageKey(classSlug);
      sessionStorage.removeItem(storageKey);
    } finally {
      setIsInitialized(true);
    }
  }, [classSlug]);

  // Save state to sessionStorage whenever it changes
  useEffect(() => {
    if (isInitialized) {
      try {
        const storageKey = getStorageKey(classSlug);
        sessionStorage.setItem(storageKey, JSON.stringify(state));
      } catch (error) {
        console.error('Error saving class date filter state:', error);
      }
    }
  }, [state, isInitialized, classSlug]);

  const updateState = useCallback((newState: Partial<ClassDateFilterState>) => {
    setState((prev) => ({ ...prev, ...newState }));
  }, []);

  const setSelectedDate = useCallback((date: Date | null) => {
    setState((prev) => ({ ...prev, selectedDate: date }));
  }, []);

  const setSelectedInstructorIds = useCallback((instructorIds: string[]) => {
    setState((prev) => ({ ...prev, selectedInstructorIds: instructorIds }));
  }, []);

  const setIsRestoredFromStorage = useCallback((value: boolean) => {
    setState((prev) => ({ ...prev, isRestoredFromStorage: value }));
  }, []);

  const resetToDefaults = useCallback(() => {
    const defaultState = getDefaultState();
    setState(defaultState);
  }, []);

  const clearStoredState = useCallback(() => {
    const storageKey = getStorageKey(classSlug);
    sessionStorage.removeItem(storageKey);
    resetToDefaults();
  }, [classSlug, resetToDefaults]);

  const markAsNotRestored = useCallback(() => {
    setState((prev) => ({ ...prev, isRestoredFromStorage: false }));
  }, []);

  return {
    state,
    isInitialized,
    updateState,
    setSelectedDate,
    setSelectedInstructorIds,
    setIsRestoredFromStorage,
    resetToDefaults,
    clearStoredState,
    markAsNotRestored,
  };
};
